/**
 * Generic, accessible data table with built-in loading and empty states.
 * Horizontally scrollable on small screens (the wrapper owns the overflow).
 *
 * Columns can opt into client-side sorting by providing a `sortAccessor`. The
 * header then becomes a button that cycles unsorted → default direction →
 * opposite → unsorted, with a ▲/▼ indicator. Sorting is display-only (a copy of
 * `rows` is sorted); nullish values always sort last.
 */
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import Spinner from '@/components/common/Spinner';
import EmptyState from '@/components/common/EmptyState';

export interface Column<T> {
  /** Property key on the row, or an arbitrary id when using `render`. */
  key: keyof T | string;
  header: ReactNode;
  /** Custom cell renderer; defaults to `String(row[key])`. */
  render?: (row: T, rowIndex: number) => ReactNode;
  /** Optional per-column classes (applied to header + cells). */
  className?: string;
  /** Text alignment for the column. */
  align?: 'left' | 'center' | 'right';
  /**
   * Enable sorting on this column. Returns the comparable value for a row
   * (number for numeric columns, string for text). Nullish → sorts last.
   */
  sortAccessor?: (row: T) => number | string | null | undefined;
  /** Direction applied on the first click of this column (default 'asc'). */
  defaultSortDir?: 'asc' | 'desc';
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyMessage?: string;
  /** Stable key extractor; defaults to the row index. */
  rowKey?: (row: T, index: number) => string | number;
  className?: string;
  /** Enable a leading checkbox column for row selection. */
  selectable?: boolean;
  /** Selected row keys (as strings). */
  selected?: string[];
  onSelectedChange?: (ids: string[]) => void;
}

interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

const alignClass: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const flexAlignClass: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

/** Render a single cell's content (custom renderer or raw value). */
function renderCell<T>(column: Column<T>, row: T, index: number): ReactNode {
  if (column.render) return column.render(row, index);
  const value = (row as Record<string, unknown>)[column.key as string];
  if (value === null || value === undefined) return '—';
  return String(value);
}

/** True when a value is "empty" for sort purposes (always sorted last). */
function isNullish(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

/**
 * Generic table. Pass a typed `columns` array and `rows`; the component handles
 * loading (spinner), empty (EmptyState), responsive overflow and optional
 * client-side sorting (per-column `sortAccessor`).
 */
export default function Table<T>({
  columns,
  rows,
  loading = false,
  emptyMessage = 'Không có dữ liệu để hiển thị.',
  rowKey,
  className = '',
  selectable = false,
  selected,
  onSelectedChange,
}: TableProps<T>): ReactElement {
  const [sort, setSort] = useState<SortState | null>(null);

  const toggleSort = (col: Column<T>): void => {
    const id = String(col.key);
    const first = col.defaultSortDir ?? 'asc';
    const opposite = first === 'asc' ? 'desc' : 'asc';
    setSort((prev) => {
      if (!prev || prev.key !== id) return { key: id, dir: first };
      if (prev.dir === first) return { key: id, dir: opposite };
      return null; // third click clears the sort
    });
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => String(c.key) === sort.key);
    if (!col?.sortAccessor) return rows;
    const acc = col.sortAccessor;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      const na = isNullish(va);
      const nb = isNullish(vb);
      if (na && nb) return 0;
      if (na) return 1; // nulls always last
      if (nb) return -1;
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), 'vi', { numeric: true, sensitivity: 'base' });
      }
      return cmp * factor;
    });
  }, [rows, sort, columns]);

  const keyOf = (row: T, i: number): string => String(rowKey ? rowKey(row, i) : i);
  const selectedSet = new Set(selected ?? []);
  const visibleIds = selectable ? sortedRows.map((r, i) => keyOf(r, i)) : [];
  const allSelected = selectable && visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const toggleAll = (): void => {
    if (!onSelectedChange) return;
    onSelectedChange(
      allSelected
        ? (selected ?? []).filter((id) => !visibleIds.includes(id))
        : [...new Set([...(selected ?? []), ...visibleIds])],
    );
  };
  const toggleOne = (id: string): void => {
    if (!onSelectedChange) return;
    onSelectedChange(selectedSet.has(id) ? (selected ?? []).filter((x) => x !== id) : [...(selected ?? []), id]);
  };
  const colCount = columns.length + (selectable ? 1 : 0);

  return (
    <div className={`overflow-x-auto rounded-lg border border-gray-200 ${className}`.trim()}>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {selectable && (
              <th scope="col" className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Chọn tất cả"
                />
              </th>
            )}
            {columns.map((col, i) => {
              const id = String(col.key);
              const align = col.align ?? 'left';
              const sortable = typeof col.sortAccessor === 'function';
              const active = sort?.key === id;
              const indicator = !sortable ? null : active ? (sort?.dir === 'asc' ? '↑' : '↓') : '↕';
              return (
                <th
                  key={`${id}-${i}`}
                  scope="col"
                  aria-sort={!active ? undefined : sort?.dir === 'asc' ? 'ascending' : 'descending'}
                  className={`px-4 py-3 font-semibold text-gray-600 ${alignClass[align]} ${col.className ?? ''}`.trim()}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className={`group inline-flex items-center gap-1 ${flexAlignClass[align]} font-semibold ${active ? 'text-brand-700' : 'text-gray-600 hover:text-gray-900'}`}
                      title="Nhấn để sắp xếp"
                    >
                      <span>{col.header}</span>
                      <span className={active ? 'text-brand-600' : 'text-gray-300 group-hover:text-gray-400'} aria-hidden="true">
                        {indicator}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {loading ? (
            <tr>
              <td colSpan={colCount} className="px-4 py-10">
                <div className="flex items-center justify-center text-brand-600">
                  <Spinner />
                </div>
              </td>
            </tr>
          ) : sortedRows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="px-0 py-0">
                <EmptyState message={emptyMessage} />
              </td>
            </tr>
          ) : (
            sortedRows.map((row, rowIndex) => {
              const rk = keyOf(row, rowIndex);
              return (
              <tr key={rk} className={selectedSet.has(rk) ? 'bg-brand-50/50' : 'hover:bg-gray-50'}>
                {selectable && (
                  <td className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      checked={selectedSet.has(rk)}
                      onChange={() => toggleOne(rk)}
                      aria-label="Chọn dòng"
                    />
                  </td>
                )}
                {columns.map((col, colIndex) => (
                  <td
                    key={`${String(col.key)}-${colIndex}`}
                    className={`whitespace-nowrap px-4 py-3 text-gray-700 ${alignClass[col.align ?? 'left']} ${col.className ?? ''}`.trim()}
                  >
                    {renderCell(col, row, rowIndex)}
                  </td>
                ))}
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
